import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, useTheme } from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import GameCard from '../components/GameCard';
import { SPORTS_COLLECTIONS } from '../constants';

function GamesList() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedClusters, setExpandedClusters] = useState(new Set());
  const theme = useTheme();

  useEffect(() => {
    const fetchAllGames = async () => {
      try {
        // Fetch games from all sports collections in parallel
        const fetchPromises = SPORTS_COLLECTIONS.map(async (sport) => {
          const sportCollection = collection(db, sport);
          const snapshot = await getDocs(sportCollection);
          return snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              ...data,
              id: doc.id,
              sport: sport
            };
          });
        });

        // Wait for all fetches to complete
        const gamesArrays = await Promise.all(fetchPromises);
        
        // Merge all games into a single array
        const allGames = gamesArrays.flat();
        
        // Sort by scrape_date (most recent first)
        const sortedGames = allGames.sort((a, b) => 
          b.scrape_date.toDate() - a.scrape_date.toDate()
        );
        
        // Group games by cluster_id
        const groupedGames = sortedGames.reduce((acc, game) => {
          const clusterId = game.cluster_id || 'unclustered';
          if (!acc[clusterId]) {
            acc[clusterId] = [];
          }
          acc[clusterId].push(game);
          return acc;
        }, {});
        
        setGames(groupedGames);
      } catch (error) {
        console.error("Error fetching games:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllGames();
  }, []);

  const toggleCluster = (clusterId) => {
    setExpandedClusters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clusterId)) {
        newSet.delete(clusterId);
      } else {
        newSet.add(clusterId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Container>
        <Typography variant="h5" sx={{ textAlign: 'center', mt: 4 }}>
          Loading...
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: theme.palette.grey[100],
      pt: 3,
      pb: 6
    }}>
      <Container maxWidth="md">
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 4,
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}
        >
          Hidden Score
        </Typography>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}>
          {Object.entries(games).map(([clusterId, clusterGames]) => (
            <Box key={clusterId}>
              <Box
                onClick={() => toggleCluster(clusterId)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                  userSelect: 'none'
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  {expandedClusters.has(clusterId) ? '▼' : '▶'}
                  {clusterId} ({clusterGames.length} games)
                </Typography>
              </Box>
              {expandedClusters.has(clusterId) && (
                <Box sx={{ pl: 3 }}>
                  {clusterGames.map(game => (
                    <GameCard key={`${game.sport}-${game.id}`} game={game} />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default GamesList; 